/**
 * The price of every cart line — ONE rule for the cart panel (desk and phone
 * sheet), the phone's mini-cart bar, the weight window, the customer display,
 * the held cart and the quote (1.58; spec §1.3).
 *
 * A line may have a *special* price: the price group of the cart's customer
 * (lib/price-groups.ts), else its wholesale price when the cart's manual
 * wholesale switch is on — or the product's club price when the cart's
 * customer is a club member (club A, 1.63), whichever is lower (a tie keeps
 * the group or wholesale price: it needs no membership on the server). The owner's promotions (086) run over every line at
 * the retail price, exactly as before. Then, per promotion, the cart takes the
 * cheaper of
 *   A — the promotion's lines as the promotion prices them, and
 *   B — the same lines each at its special price (retail when it has none),
 *       with no promotion;
 * a tie keeps the promotion. The choice is made for ALL the lines of a
 * promotion at once (Codex's design review #2): a line at its special price
 * that kept its share of a two-product bundle would send the server less than
 * the bundle's floor and be refused (PROMO_PRICE_BELOW). A line with no
 * promotion simply takes its special price. The customer never pays more than
 * a walk-in: he pays min(A, B) ≤ A.
 *
 * THE SERVER DECIDES what is allowed (090 retail / wholesale, 179 the group
 * price of the customer on the sale, 190 the club price of a member, 086 the
 * promotion floor); this file only
 * chooses which allowed price the till sends.
 */
import type { PriceGroup } from './mock-data'
import { applyPromotions, type PromoResult, type Promotion } from './promotions'
import { groupUnitPrice } from './price-groups'
import { lineAmount, roundMoney } from './money-round'

/** club: the cart's customer is a club member the server just confirmed (online only; absent = no). */
export type PriceMode = { wholesale: boolean; group: PriceGroup | null; club?: boolean }

export type CartPriceLine = {
  product_id: string; quantity: number; unit_price: number; wholesale_price?: number | null
  /** the product's club price (188) — a member's special price */
  club_price?: number | null
  /** a repair charged at the till (130): its own price — no group, no promotion */
  fixed?: boolean
}

export type PricedLine = {
  /** the unit price sent to create_sale */
  unit_price: number
  /** the product's retail price — the struck price and the saving */
  retail: number
  special: 'group' | 'wholesale' | 'club' | null
  promo: PromoResult | null
  /** the line as sale_items stores it, to the tetri (a promoted line as computed) */
  amount: number
}

/** The line's special price — only when it is lower than retail. */
export function specialPrice(
  line: { unit_price: number; wholesale_price?: number | null; club_price?: number | null },
  mode: PriceMode,
): { price: number; kind: 'group' | 'wholesale' | 'club' } | null {
  const base = groupOrWholesale(line, mode)
  const club = mode.club && line.club_price != null && line.club_price < line.unit_price - 0.005 ? line.club_price : null
  if (club != null && (base == null || club < base.price - 0.005)) return { price: club, kind: 'club' }
  return base
}

function groupOrWholesale(
  line: { unit_price: number; wholesale_price?: number | null },
  mode: PriceMode,
): { price: number; kind: 'group' | 'wholesale' } | null {
  if (mode.group?.active) {
    const price = groupUnitPrice(mode.group, line)
    return price < line.unit_price - 0.005 ? { price, kind: 'group' } : null
  }
  if (mode.wholesale && line.wholesale_price != null && line.wholesale_price < line.unit_price - 0.005) {
    return { price: line.wholesale_price, kind: 'wholesale' }
  }
  return null
}

export function priceCart(
  lines: readonly CartPriceLine[],
  promotions: readonly Promotion[],
  mode: PriceMode,
  now: Date = new Date(),
): Map<string, PricedLine> {
  const live = lines.filter(l => l.quantity > 0 && !l.fixed)
  const promos = applyPromotions(live.map(l => ({ product_id: l.product_id, quantity: l.quantity, unit_price: l.unit_price })), [...promotions], now)

  // per promotion: A (as promoted) against B (each line at its special price, else retail)
  const leave = new Set<string>()
  const byPromo = new Map<string, CartPriceLine[]>()
  for (const l of live) {
    const r = promos.get(l.product_id)
    if (r) byPromo.set(r.promotion_id, [...(byPromo.get(r.promotion_id) ?? []), l])
  }
  for (const ls of byPromo.values()) {
    const a = ls.reduce((s, l) => s + l.unit_price * l.quantity - (promos.get(l.product_id)?.promo_discount ?? 0), 0)
    const b = ls.reduce((s, l) => s + (specialPrice(l, mode)?.price ?? l.unit_price) * l.quantity, 0)
    if (b < a - 0.005) ls.forEach(l => leave.add(l.product_id))
  }

  const out = new Map<string, PricedLine>()
  for (const l of lines) {
    const promo = leave.has(l.product_id) ? undefined : promos.get(l.product_id)
    if (promo) {
      out.set(l.product_id, {
        unit_price: l.unit_price, retail: l.unit_price, special: null, promo,
        amount: lineAmount(l.unit_price * l.quantity - promo.promo_discount, promo.promo_discount > 0),
      })
      continue
    }
    const sp = l.fixed ? null : specialPrice(l, mode)
    const unit = sp?.price ?? l.unit_price
    out.set(l.product_id, { unit_price: unit, retail: l.unit_price, special: sp?.kind ?? null, promo: null, amount: lineAmount(unit * l.quantity, false) })
  }
  return out
}

/** What the customer kept: by his group, by the wholesale switch, by the club, by promotions (box prices included). */
export function cartSavings(
  lines: readonly CartPriceLine[], priced: Map<string, PricedLine>,
): { group: number; wholesale: number; club: number; promo: number } {
  let group = 0, wholesale = 0, club = 0, promo = 0
  for (const l of lines) {
    const p = priced.get(l.product_id)
    if (!p) continue
    if (p.promo) promo += p.promo.promo_discount
    const kept = (p.retail - p.unit_price) * l.quantity
    if (p.special === 'group') group += kept
    if (p.special === 'wholesale') wholesale += kept
    if (p.special === 'club') club += kept
  }
  return { group: roundMoney(group), wholesale: roundMoney(wholesale), club: roundMoney(club), promo: roundMoney(promo) }
}

/** „25 ც = 2 ყუთი + 1 ც" — whole boxes and the rest (no box size, no boxes). */
export function packSplit(quantity: number, packQty: number | null | undefined): { boxes: number; rest: number } {
  const n = packQty ?? 0
  if (n < 2) return { boxes: 0, rest: quantity }
  const boxes = Math.floor(quantity / n + 1e-9)
  return { boxes, rest: roundMoney(quantity - boxes * n) }
}

/** The cart's total before the cart discount and the bonus — every place that shows it adds the same lines. */
export function cartTotalOf(lines: readonly CartPriceLine[], priced: Map<string, PricedLine>): number {
  return roundMoney(lines.reduce((s, l) => s + (priced.get(l.product_id)?.amount ?? 0), 0))
}
